import { useState } from 'react';

import { Modal } from '@/components/Modal';
import { percent, shortDate } from '@/lib/format';
import { useDailyLogActions, useDailyLogProgressPreview } from '@/lib/queries';
import type { DailyLog, Uuid } from '@/lib/types';

/**
 * 제출 전 프리뷰 — 「이 일보를 반영하면 무엇이 얼마가 되는가」(DOC-87 §3-8 · DOC-29 §4).
 *
 * <p>제출은 공정율 스냅샷을 움직인다. 눌러 놓고 나서 「그게 그렇게 되는 줄 몰랐다」가 없게, 무엇이
 * 얼마로 바뀌는지를 먼저 보여 주고 제목 줄의 버튼으로 제출한다.
 *
 * <p>수기 진행율과 부딪힌 공정({@code conflict})은 기본으로 <b>건너뛴다.</b> 덮어쓰려면 사람이 스위치를
 * 켠다 — 누가 손으로 고쳐 둔 값을 일보가 조용히 밟고 지나가면 안 된다.
 */
export function SubmitPreviewModal({
  siteId,
  log,
  onSubmitted,
  onClose,
}: {
  siteId: Uuid;
  log: DailyLog;
  onSubmitted: (skipped: string[]) => void;
  onClose: () => void;
}) {
  const preview = useDailyLogProgressPreview(siteId, log.id);
  const { submit } = useDailyLogActions(siteId);
  const [overwriteManual, setOverwriteManual] = useState(false);
  const [failed, setFailed] = useState(false);

  const changes = preview.data?.changes ?? [];
  const conflicts = changes.filter((it) => it.conflict);
  const before = preview.data?.rollupBefore ?? null;
  const after = preview.data?.rollupAfter ?? null;

  async function onSubmit() {
    setFailed(false);
    try {
      const result = await submit.mutateAsync({ id: log.id, overwriteManual });
      onSubmitted(result.skipped);
    }
    catch {
      setFailed(true);
    }
  }

  return (
    <Modal
      title={`${shortDate(log.logDate)} 일보 제출`}
      maxWidth="36rem"
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn-secondary px-3 py-1.5 text-ui-xs" onClick={onClose} disabled={submit.isPending}>
            닫기
          </button>
          <button
            type="button"
            className="btn-primary px-3 py-1.5 text-ui-xs"
            onClick={onSubmit}
            disabled={submit.isPending || preview.isPending}
          >
            {submit.isPending ? '제출 중입니다…' : '제출'}
          </button>
        </>
      }
    >
      {failed && (
        <p className="mb-3 text-ui-xs" style={{ color: 'var(--danger)' }}>제출하지 못했습니다.</p>
      )}

      {preview.isPending && (
        <p className="py-4 text-center text-ui-sm" style={{ color: 'var(--text-muted)' }}>불러오는 중입니다…</p>
      )}

      {preview.isError && (
        <p className="py-4 text-center text-ui-sm" style={{ color: 'var(--text-muted)' }}>지금은 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</p>
      )}

      {preview.data !== undefined && (
        <>
          {/* 전체 공정율 — 지금 → 반영 뒤. 이 한 줄이 제출의 무게다. */}
          <div className="mb-3 grid grid-cols-2 gap-2">
            <Stat label="지금 공정율" value={percent(before?.actualRate)} />
            <Stat label="반영 뒤" value={percent(after?.actualRate)} accent />
          </div>

          {changes.length === 0 ? (
            <p className="py-3 text-center text-ui-sm" style={{ color: 'var(--text-muted)' }}>공정율이 바뀌는 공정이 없습니다.</p>
          ) : (
            <ul className="flex flex-col divide-y" style={{ borderColor: 'var(--border)' }}>
              {changes.map((it) => (
                <li key={it.wbsItemId} className="flex items-center justify-between gap-3 py-2 text-ui-sm">
                  <div className="min-w-0">
                    <div className="truncate">{it.code} {it.name}</div>
                    {it.skipReason !== null && (
                      <div className="text-ui-2xs" style={{ color: 'var(--danger)' }}>{it.skipReason}</div>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <span style={{ color: 'var(--text-muted)' }}>{percent(it.currentRate)}</span>
                    <span className="mx-1" style={{ color: 'var(--text-muted)' }}>→</span>
                    <span className="font-semibold" style={it.conflict ? { color: 'var(--danger)' } : undefined}>{percent(it.nextRate)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {conflicts.length > 0 && (
            <label className="mt-3 flex cursor-pointer items-center gap-2 text-ui-sm">
              <input type="checkbox" checked={overwriteManual} onChange={(e) => setOverwriteManual(e.target.checked)} />
              <span>수기 진행율 {conflicts.length}건을 이 일보 값으로 덮어쓴다</span>
            </label>
          )}
        </>
      )}
    </Modal>
  );
}

function Stat({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-md p-2" style={{ background: accent ? 'var(--accent-bg)' : 'var(--bg-hover)' }}>
      <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="text-ui-lg font-semibold" style={accent ? { color: 'var(--accent)' } : undefined}>{value}</div>
    </div>
  );
}
